import {
  ROUTE_PERMISSIONS,
  normalizeRoleCode,
  resolveUserRoleName,
} from "../constants";

const resolveRole = (userOrRoleName) => {
  if (typeof userOrRoleName === "object" && userOrRoleName !== null) {
    return resolveUserRoleName(userOrRoleName);
  }
  return userOrRoleName || "";
};

/** Normalize role for permission checks (handles Admin, Caregiver, Office Assistant, etc.). */
export const roleCode = (userOrRoleName) =>
  normalizeRoleCode(resolveRole(userOrRoleName));

/** Route guard + sidebar — true when role may open `path`. */
export const canAccessRoute = (userOrRoleName, path) => {
  const code = roleCode(userOrRoleName);
  if (code === "SUPERADMIN") {
    return path === "/platform-billing" || path === "/change-password" || path === "/profile";
  }
  const allowed = ROUTE_PERMISSIONS[path];
  if (!allowed?.length) return true;
  return allowed.some((role) => normalizeRoleCode(role) === code);
};

/** Dashboard access tiers — drives sidebar Dashboard link + tab visibility. */
export const DASHBOARD_ACCESS = {
  NONE: "none",
  /** CG/CNA — clock-in overview + own time card only */
  OVERVIEW_ACTIVITY: "overview_activity",
  /** OA — patient statistics + overview + activity (no employee census) */
  OFFICE_FULL: "office_full",
  /** Admin — all dashboard tabs including employee + patient statistics */
  ADMIN_FULL: "admin_full",
};

export const getDashboardAccess = (userOrRoleName) => {
  const code = roleCode(userOrRoleName);
  if (code === "ADMIN") return DASHBOARD_ACCESS.ADMIN_FULL;
  if (code === "OA") return DASHBOARD_ACCESS.OFFICE_FULL;
  if (code === "CG" || code === "CNA") return DASHBOARD_ACCESS.OVERVIEW_ACTIVITY;
  return DASHBOARD_ACCESS.NONE;
};

export const canAccessDashboard = (userOrRoleName) =>
  getDashboardAccess(userOrRoleName) !== DASHBOARD_ACCESS.NONE;

/** Dashboard → Patient Statistics (tenant census). Admin + OA. */
export const canViewPatientStatistics = (userOrRoleName) => {
  const access = getDashboardAccess(userOrRoleName);
  return (
    access === DASHBOARD_ACCESS.ADMIN_FULL ||
    access === DASHBOARD_ACCESS.OFFICE_FULL
  );
};

/** Dashboard → Employee Statistics (staff counts). Admin only. */
export const canViewEmployeeStatistics = (userOrRoleName) =>
  getDashboardAccess(userOrRoleName) === DASHBOARD_ACCESS.ADMIN_FULL;

/** Default dashboard tab when the active tab is not allowed for this role. */
export const defaultDashboardTabForRole = (userOrRoleName) => {
  if (canViewEmployeeStatistics(userOrRoleName)) return "Dashboard";
  if (canViewPatientStatistics(userOrRoleName)) return "PatientStats";
  return "Overview";
};
